import { useEffect, useState, useCallback } from 'react';
import { Database, BrainCircuit, Activity, ShieldCheck, Link2, Unlink, Zap, CheckCircle2, FolderTree, Terminal, Upload, Wifi, WifiOff, RefreshCw } from 'lucide-react';

interface BillingStatus {
  plan_id: string;
  tokens_used: number;
  tokens_saved: number;
  tokens_quota: number;
  sessions_active: number;
  sessions_quota: number;
}

interface ProjectInfo {
  name: string;
  path: string;
  branch: string;
  status: string;
  uploadedAt?: string;
}

interface AgentStatus {
  connected: boolean;
  workspace: string;
  pairingToken: string;
}

function LocalAgentPairingCard() {
  const [pairedToken, setPairedToken] = useState<string | null>(() => localStorage.getItem('eric_mec_local_token'));
  const [inputToken, setInputToken] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [uploadingPath, setUploadingPath] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<Record<string, 'ok' | 'error'>>({});

  // Poll Local Agent port 8888 — project list + connection status
  const pollAgent = useCallback(async () => {
    try {
      const headers: Record<string, string> = pairedToken ? { 'Authorization': `Bearer ${pairedToken}` } : {};

      // Lấy trạng thái kết nối
      const statusRes = await fetch('http://localhost:8888/v1/status', { headers });
      if (statusRes.ok) {
        const s = await statusRes.json();
        setAgentStatus(s);
      }

      // Lấy danh sách project đã scan
      const projRes = await fetch('http://localhost:8888/v1/projects', { headers });
      if (projRes.ok) {
        const data = await projRes.json();
        if (Array.isArray(data)) setProjects(data);
      }
    } catch {
      // Agent chưa chạy hoặc chưa kết nối
      setAgentStatus(null);
    }
  }, [pairedToken]);

  useEffect(() => {
    pollAgent();
    const interval = setInterval(pollAgent, 2500);
    return () => clearInterval(interval);
  }, [pollAgent]);

  // Sync state to Ericoder via Eric MEC backend
  useEffect(() => {
    if (pairedToken && projects.length > 0) {
      const token = localStorage.getItem('eric_token');
      if (token) {
        fetch('/v1/admin/sync-ericoder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ localToken: pairedToken, workspace: projects[0].name })
        }).catch(() => {});
      }
    }
  }, [pairedToken, projects]);

  const handlePair = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputToken || inputToken.length < 8) {
      setStatusMsg('Please enter a valid Local Pairing Token from the Windows app.');
      return;
    }
    localStorage.setItem('eric_mec_local_token', inputToken.trim());
    setPairedToken(inputToken.trim());
    setStatusMsg('');
    window.dispatchEvent(new Event('eric_mec_token_change'));
  };

  const handleUnpair = () => {
    localStorage.removeItem('eric_mec_local_token');
    setPairedToken(null);
    setInputToken('');
    window.dispatchEvent(new Event('eric_mec_token_change'));
  };

  const handleDisconnect = async () => {
    try {
      await fetch('http://localhost:8080/disconnect', { method: 'POST' });
      setAgentStatus(null);
    } catch { /* ignore */ }
  };

  const handleUpload = async (proj: ProjectInfo) => {
    setUploadingPath(proj.path);
    try {
      const res = await fetch('http://localhost:8888/v1/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: proj.path }),
      });
      const data = await res.json() as { ok: boolean; error?: string };
      setUploadResults(prev => ({ ...prev, [proj.path]: data.ok ? 'ok' : 'error' }));
    } catch {
      setUploadResults(prev => ({ ...prev, [proj.path]: 'error' }));
    } finally {
      setUploadingPath(null);
      pollAgent(); // refresh project list (uploadedAt)
    }
  };

  const isAgentOnline = agentStatus !== null;
  const isConnected  = agentStatus?.connected === true;

  return (
    <div className="glass-panel" style={{
      padding: '24px',
      background: isConnected
        ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(6,182,212,0.05) 100%)'
        : 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, rgba(30,41,59,0.4) 100%)',
      border: isConnected ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(56,189,248,0.3)',
      boxShadow: isConnected ? '0 0 25px rgba(16,185,129,0.15)' : '0 0 20px rgba(56,189,248,0.1)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ background: isConnected ? 'rgba(16,185,129,0.2)' : 'rgba(56,189,248,0.2)', padding: '8px', borderRadius: '8px', color: isConnected ? 'var(--accent-green)' : 'var(--accent-cyan)' }}>
              {isConnected ? <ShieldCheck size={22} /> : <Link2 size={22} />}
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: isConnected ? '#4ade80' : 'var(--text-primary)' }}>
              {isConnected
                ? '🟢 Local ComputeUse Agent Paired & Active'
                : isAgentOnline
                  ? '🟡 Agent Running — Tunnel Disconnected'
                  : '⚡ Connect Local ComputeUse Agent'}
            </h3>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0, maxWidth: '650px', lineHeight: '1.5' }}>
            {isConnected
              ? `Paired & streaming. Workspace: ${agentStatus?.workspace || '—'}`
              : isAgentOnline
                ? 'Agent is online but WebSocket tunnel is not connected. Check your connection token.'
                : 'Paste your Local Pairing Token from the Windows app to authorize secure local compute.'}
          </p>
        </div>

        {/* Action buttons — header */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Agent online/offline badge */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
            background: isAgentOnline ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${isAgentOnline ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.3)'}`,
            color: isAgentOnline ? '#4ade80' : '#f87171',
          }}>
            {isAgentOnline ? <Wifi size={13} /> : <WifiOff size={13} />}
            {isAgentOnline ? 'Agent Online :8888' : 'Agent Offline'}
          </div>

          {isConnected && (
            <button
              onClick={handleDisconnect}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '8px', color: '#f87171', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
            >
              <Unlink size={16} /> Disconnect
            </button>
          )}

          {pairedToken && (
            <button
              onClick={handleUnpair}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
            >
              <Unlink size={16} /> Unpair Token
            </button>
          )}
        </div>
      </div>

      {/* Token input hoặc Connected info */}
      {!pairedToken ? (
        <form onSubmit={handlePair} style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '280px' }}>
            <input
              type="text"
              placeholder="Paste Local Pairing Token here (e.g. f98b065380b8449be7...)"
              value={inputToken}
              onChange={(e) => setInputToken(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', color: '#fff', fontSize: '14px', fontFamily: 'var(--font-mono)', outline: 'none' }}
            />
          </div>
          <button
            type="submit"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', border: '1px solid #38bdf8', borderRadius: '8px', color: '#fff', fontWeight: 600, fontSize: '14px', cursor: 'pointer', boxShadow: '0 0 15px rgba(56,189,248,0.3)' }}
          >
            <Zap size={16} /> Pair & Authorize Server
          </button>
        </form>
      ) : (
        <div style={{ marginTop: '18px' }}>
          <div style={{ padding: '14px 18px', background: 'rgba(0,0,0,0.35)', borderRadius: '8px', border: '1px dashed rgba(16,185,129,0.3)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '13px' }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Connected Token:</span>
              <div style={{ fontFamily: 'var(--font-mono)', color: '#fff', fontWeight: 600, marginTop: '2px' }}>
                {pairedToken.slice(0, 10)}...{pairedToken.slice(-6)}
              </div>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Tunnel Encryption:</span>
              <div style={{ color: '#4ade80', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={14} /> TLS 1.3 / Zero-Trust Active
              </div>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>PaaS Hand-over:</span>
              <div style={{ color: '#38bdf8', fontWeight: 600, marginTop: '2px', fontSize: '12px' }}>Auto-delegated to Ericoder</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Browse Workspace Projects ── */}
      <div style={{ marginTop: '22px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px', color: '#e2e8f0' }}>
            <FolderTree size={18} style={{ color: 'var(--accent-cyan)' }} />
            Browse Workspace Projects (Antigravity IDE & Claude Code)
          </div>
          <button
            onClick={pollAgent}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '6px', color: '#38bdf8', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          {projects.length === 0
            ? isAgentOnline
              ? '✅ Agent is online. Click "Browse Workspace" in the desktop app to add projects...'
              : '⚠️ Eric MEC ComputeUse Agent is not running. Please launch the desktop app first.'
            : `${projects.length} project(s) found. Click a project to stream its terminal.`}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {projects.length > 0 ? projects.map((proj) => {
            const uploadResult = uploadResults[proj.path];
            const isUploading = uploadingPath === proj.path;
            return (
              <div
                key={proj.path}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '10px' }}
              >
                {/* Project header */}
                <div
                  onClick={() => {
                    const el = document.getElementById('dashTerminalStream');
                    if (el) el.innerText = `PS ${proj.path}> Connecting...\n[+] Workspace: ${proj.path}\n[+] Branch: ${proj.branch} | Status: ${proj.status}\n[Ready] Stream open for Ericoder / Antigravity IDE.`;
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '14px', color: '#38bdf8' }}>
                    📁 {proj.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px', wordBreak: 'break-all' }}>{proj.path}</div>
                </div>

                {/* Branch + Status */}
                <div style={{ display: 'flex', gap: '8px', fontSize: '11px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '8px' }}>
                  <span style={{ color: '#a855f7' }}>🌿 {proj.branch}</span>
                  <span style={{ color: '#4ade80' }}>● {proj.status}</span>
                  {proj.uploadedAt && (
                    <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
                      ☁ {new Date(proj.uploadedAt).toLocaleTimeString()}
                    </span>
                  )}
                </div>

                {/* Upload status badge */}
                {uploadResult && (
                  <div style={{
                    fontSize: '11px', padding: '4px 8px', borderRadius: '4px', fontWeight: 600,
                    background: uploadResult === 'ok' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                    color: uploadResult === 'ok' ? '#4ade80' : '#f87171',
                    border: `1px solid ${uploadResult === 'ok' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  }}>
                    {uploadResult === 'ok' ? '✅ Uploaded to Eric MEC' : '❌ Upload failed'}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {/* Upload to Eric MEC */}
                  <button
                    onClick={() => handleUpload(proj)}
                    disabled={isUploading}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                      padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: isUploading ? 'wait' : 'pointer',
                      background: isUploading ? 'rgba(56,189,248,0.08)' : 'linear-gradient(135deg, rgba(56,189,248,0.2), rgba(99,102,241,0.2))',
                      border: '1px solid rgba(56,189,248,0.4)', color: '#38bdf8',
                      opacity: isUploading ? 0.7 : 1,
                    }}
                  >
                    <Upload size={11} />
                    {isUploading ? 'Uploading...' : 'Upload to Eric MEC'}
                  </button>

                  {/* Launch Ericoder IDE */}
                  <button
                    onClick={() => window.open(`https://coder.ericai.online/?workspace=${encodeURIComponent(proj.path)}#token=${encodeURIComponent(pairedToken || '')}`, '_blank')}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: '1px solid #fbbf24', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    🚀 Ericoder IDE
                  </button>
                </div>
              </div>
            );
          }) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', gridColumn: '1 / -1', padding: '12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isAgentOnline ? (
                <>
                  <span style={{ color: '#4ade80' }}>●</span>
                  Agent online — Click "Browse Workspace" in the desktop app to scan a project folder.
                </>
              ) : (
                <>
                  <span style={{ color: '#f87171' }}>●</span>
                  No projects found. Launch Eric MEC ComputeUse desktop app first.
                </>
              )}
            </div>
          )}
        </div>

        {/* Terminal Stream */}
        <div style={{ background: '#090d16', border: '1px solid #1e293b', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: '#111827', borderBottom: '1px solid #1e293b', fontSize: '12px', color: '#94a3b8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Terminal size={14} style={{ color: 'var(--accent-cyan)' }} />
              <span>Terminal Stream Output (PowerShell Exec Wrapper)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isConnected ? '#4ade80' : '#f87171' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isConnected ? '#4ade80' : '#f87171', display: 'inline-block' }}></span>
              {isConnected ? 'Live Tunnel Connected' : 'Tunnel Offline'}
            </div>
          </div>
          <div id="dashTerminalStream" style={{ padding: '14px', fontFamily: "'Consolas', 'Courier New', monospace", fontSize: '13px', color: '#38bdf8', lineHeight: '1.5', minHeight: '120px', maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
            {`PS D:\\ERI CODE> Ready. Click any workspace folder above to stream local execution context...`}
          </div>
        </div>

        {/* Authorized Frontend Connections */}
        <div style={{ marginTop: '22px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px', color: '#e2e8f0', marginBottom: '12px' }}>
            <span style={{ color: '#f59e0b' }}>🔐</span> Authorized Frontend Connections
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '8px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(56,189,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🚀</div>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Ericoder AI IDE Workspace <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(74,222,128,0.2)', color: '#4ade80', borderRadius: '12px' }}>🟢 Active Stream</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Permissions: Read/Write & Terminal Execution | Shared Session: eric@ericai.online</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Last Active: Just now</span>
                <button
                  onClick={() => alert('Thu hồi quyền truy cập thành công! Luồng Local Tunnel tới Ericoder đã được đóng lại.')}
                  style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '6px', color: '#f87171', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Revoke Tunnel
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.15)', borderRadius: '8px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🌐</div>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Web Dashboard (mec.ericai.online) <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(56,189,248,0.2)', color: '#38bdf8', borderRadius: '12px' }}>🔵 Master Controller</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Permissions: Full Administrative & Billing Management</div>
                </div>
              </div>
              <span style={{ fontSize: '12px', color: '#4ade80', fontWeight: 600 }}>Current Session</span>
            </div>
          </div>
        </div>
      </div>

      {statusMsg && (
        <div style={{ marginTop: '12px', color: '#f87171', fontSize: '13px', fontWeight: 500 }}>⚠️ {statusMsg}</div>
      )}
    </div>
  );
}

export function DashboardPage() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Gọi API tương đối (sẽ được proxy ở local, hoặc gọi thẳng ở production mec.ericai.online)
    fetch('/v1/billing/status')
      .then(res => res.json())
      .then(data => {
        setStatus(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch billing status', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div style={{ color: 'var(--text-muted)' }}>Loading context metrics...</div>;
  }

  // Nếu API fail (ví dụ chưa bật Worker), fallback data
  const data = status || {
    tokens_used: 12300000,
    tokens_saved: 0,
    tokens_quota: 50000000,
    sessions_active: 0,
    sessions_quota: 10
  };

  const usagePercent = Math.min(100, Math.round((data.tokens_used / data.tokens_quota) * 100));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div>
        <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>Dashboard Overview</h2>
        <p style={{ color: 'var(--text-secondary)' }}>Welcome back. Here's what your agents have been up to.</p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        
        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(124,58,237,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-primary)' }}>
              <Database size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Virtual Context Stored</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{(data.tokens_used / 1000000).toFixed(1)}M <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: 400 }}>tokens</span></div>
          <div style={{ marginTop: '12px', background: 'rgba(255,255,255,0.05)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ background: 'var(--gradient-main)', width: `${usagePercent}%`, height: '100%' }}></div>
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'right' }}>{usagePercent}% of {(data.tokens_quota / 1000000).toFixed(0)}M Quota</div>
        </div>

        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(6,182,212,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-cyan)' }}>
              <BrainCircuit size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Tokens Saved (This Month)</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{data.tokens_saved.toLocaleString()}</div>
          <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--accent-green)' }}>
            ≈ ${(data.tokens_saved * 0.00003).toFixed(2)} saved on LLM API costs
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(16,185,129,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-green)' }}>
              <Activity size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Active Agent Sessions</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{data.sessions_active} <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: 400 }}>/ {data.sessions_quota}</span></div>
          
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '16px 0' }}>
            No active sessions. Start an agent to see it here.
          </div>
        </div>

      </div>

      {/* Local ComputeUse Agent Pairing Panel */}
      <LocalAgentPairingCard />

      {/* Recent Activity */}
      <div className="glass-panel" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Recent Memory Updates</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px' }}>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Time</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Action</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Agent</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Status</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '14px' }}>
            <tr>
              <td colSpan={4} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                No recent memory updates. Connect your MCP agent to get started.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
